/**
 * PEStart Dashboard — Anlaufphase auf Live-Calc + Privatkostenkompass.
 */

import React, { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useAppStore from '../../store/appStore';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import usePEStartStore from '../../store/usePEStartStore';
import { computePEStartDashboard } from './peStartCalcAdapter';
import { computePEStartAufbauPlan } from './peStartPlanungsBridge';
import { PEStartPlanungsrechnerPicker } from './PEStartAufbauPlan';

export type PEStartDashboardProps = {
    className?: string;
};

const PEStartDashboard: React.FC<PEStartDashboardProps> = ({ className }) => {
    const calc = useCalculationsSelector() as any;
    const privateCostData = useAppStore((s) => s.clientData?.privateCostData);
    const clientData = useAppStore((s) => s.clientData);
    const setClientData = useAppStore((s) => s.setClientData);
    const settings = useAppStore((s) => s.settings);
    const appSlice = useAppStore(
        useShallow((s) => ({
            planungsAnalysePrognose: s.planungsAnalysePrognose,
            planungsAnalyseArchiv: s.planungsAnalyseArchiv,
            planungsAnalyseActiveSzenarioId: s.planungsAnalyseActiveSzenarioId,
            planungsAnalyseBasisDatenTyp: s.planungsAnalyseBasisDatenTyp,
            peProfitDataVersion: s.peProfitDataVersion,
            clientData: s.clientData,
        }))
    );

    const peState = usePEStartStore(
        useShallow((s) => ({
            foundingType: s.foundingType,
            subsidies: s.subsidies,
            investments: s.investments,
            staffFlags: s.staffFlags,
            loanPlan: s.loanPlan,
            selectedPlanungsrechnerSzenarioId: s.selectedPlanungsrechnerSzenarioId,
        }))
    );
    const setFoundingType = usePEStartStore((s) => s.setFoundingType);
    const setSelectedPlanungsrechnerSzenarioId = usePEStartStore(
        (s) => s.setSelectedPlanungsrechnerSzenarioId
    );

    const aufbau = useMemo(
        () =>
            computePEStartAufbauPlan(
                appSlice,
                calc,
                clientData,
                settings,
                peState.selectedPlanungsrechnerSzenarioId
            ),
        [appSlice, calc, clientData, settings, peState.selectedPlanungsrechnerSzenarioId]
    );

    const handleSelectPlanungsrechner = (id: string | null) => {
        setSelectedPlanungsrechnerSzenarioId(id);
        if (typeof setClientData === 'function') {
            setClientData((prev: any) => ({
                ...prev,
                peStartSelectedPlanungsrechnerId: id,
            }));
        }
    };

    const verbesserungDelta = aufbau.available
        ? aufbau.summary?.umsatzDeltaMonat ?? 0
        : 0;

    const model = useMemo(
        () => computePEStartDashboard(peState, calc, privateCostData, verbesserungDelta),
        [peState, calc, privateCostData, verbesserungDelta]
    );
    const { kpis, months, breakEvenMonat, formatCurrency } = model;
    const isTakeover = model.foundingType === 'TAKEOVER';
    const hasVerbesserung = model.prognoseModus === 'TAKEOVER_PLANZIEL_PLUS_VERBESSERUNG';

    const maxBar = useMemo(() => {
        let max = 1;
        for (const row of months) {
            max = Math.max(
                max,
                row.umsatzZiel,
                row.umsatzPrognose,
                row.fixkosten + row.personalkosten + row.unternehmerlohn,
                Math.abs(row.liquiditaetssaldo)
            );
        }
        return max;
    }, [months]);

    const barPct = (value: number) =>
        `${Math.min(100, Math.max(0, (Math.abs(value) / maxBar) * 100))}%`;

    return (
        <section
            className={`min-w-0 overflow-x-hidden rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] p-5 text-base ${className ?? ''}`}
        >
            <header className="mb-6 border-b border-pe-border dark:border-[#222] pb-4">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                    PEStart · Dashboard
                </p>
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                    {isTakeover ? 'Übernahme & Break-Even' : 'Anlaufphase & Break-Even'}
                </h2>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                    Monate 1–6 auf Basis Gesamtleistungs-Logik
                    {isTakeover
                        ? hasVerbesserung
                            ? ' — Übernahme: Start am Planziel, Planungsrechner-Verbesserung moderat bis M6'
                            : ' — Übernahme: ab Monat 1 mit 100 % Planziel (keine Anlauf-Drosselung)'
                        : ' — Neugründung: schrittweise Anlaufkurve'}
                    .
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setFoundingType('NEW')}
                        className={`rounded-lg border px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider ${
                            !isTakeover
                                ? 'border-[#E6C299] bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                : 'border-pe-border-strong dark:border-[#333] text-stone-500 dark:text-slate-500 hover:border-pe-border-strong dark:hover:border-[#444] hover:text-pe-text dark:hover:text-white'
                        }`}
                    >
                        Neugründung
                    </button>
                    <button
                        type="button"
                        onClick={() => setFoundingType('TAKEOVER')}
                        className={`rounded-lg border px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider ${
                            isTakeover
                                ? 'border-[#E6C299] bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                : 'border-pe-border-strong dark:border-[#333] text-stone-500 dark:text-slate-500 hover:border-pe-border-strong dark:hover:border-[#444] hover:text-pe-text dark:hover:text-white'
                        }`}
                    >
                        Übernahme
                    </button>
                </div>
            </header>

            <div className="mb-6">
                <PEStartPlanungsrechnerPicker
                    model={aufbau}
                    selectedId={peState.selectedPlanungsrechnerSzenarioId}
                    onSelect={handleSelectPlanungsrechner}
                    compact
                />
            </div>

            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                        Umsatzziel inkl. Rücklagen +
                        {(kpis.ruecklagenFaktor * 100).toFixed(0)}%
                    </p>
                    <p className="mt-2 text-xl font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                        {formatCurrency(kpis.totpunktMonatBrutto, 0)}
                    </p>
                    <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                        Brutto / Monat · Kostendeckung{' '}
                        {formatCurrency(kpis.totpunktKostendeckungBrutto, 0)} · UL{' '}
                        {formatCurrency(kpis.unternehmerlohnKompass, 0)}
                        {kpis.unternehmerlohnSource === 'pauschale' ? ' (Pauschale)' : ' (Kompass)'}
                    </p>
                </div>

                <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                        Mindestumsatz / Std. (Brutto) bei {kpis.auslastungsziel.toFixed(0)}%
                    </p>
                    <p className="mt-2 text-xl font-black tabular-nums text-pe-text dark:text-white">
                        {formatCurrency(kpis.mindestUmsatzBrutto75, 0)}
                        <span className="ml-1 text-sm font-semibold text-stone-500 dark:text-slate-500">/ Std.</span>
                    </p>
                    <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                        Analog Gesamtleistung · Planumsatz{' '}
                        {formatCurrency(kpis.planumsatzNetto, 0)} netto · Salon{' '}
                        {kpis.monatsstundenSalon.toFixed(0)} Std./Monat
                    </p>
                </div>

                <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                        Start-Finanzbedarf (Netto)
                    </p>
                    <p className="mt-2 text-xl font-black tabular-nums text-pe-text dark:text-white">
                        {formatCurrency(kpis.startFinanzbedarf, 0)}
                    </p>
                    <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                        Annuität {formatCurrency(kpis.finanzbedarf.rateMonat, 0)} / Monat ·{' '}
                        {kpis.finanzbedarf.tilgungLaufzeitJahre} Jahre ·{' '}
                        {kpis.finanzbedarf.zinssatzProzent} % p.a.
                    </p>
                </div>
            </div>

            {/* Finanzbedarf-Aufschlüsselung */}
            <div className="mb-6 rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                    Finanzbedarf & Kreditplan
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            Investitionen / Kaufpreis
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-text dark:text-white">
                            {formatCurrency(kpis.finanzbedarf.investitionen, 0)}
                        </p>
                    </div>
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            Anlauf-Liquiditätspuffer ({kpis.finanzbedarf.anlaufPufferMonate} Mon.)
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-text dark:text-white">
                            {formatCurrency(kpis.finanzbedarf.anlaufLiquiditaetspuffer, 0)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                            Absicherung Fixkosten + Unternehmerlohn
                        </p>
                    </div>
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            − Förderungen
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                            −{formatCurrency(kpis.finanzbedarf.foerderungen, 0)}
                        </p>
                    </div>
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            Kreditbetrag
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-text dark:text-white">
                            {formatCurrency(kpis.finanzbedarf.kreditbetrag, 0)}
                        </p>
                    </div>
                    <div className="rounded-lg border border-[#E6C299]/30 bg-[#E6C299]/10 px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]/80">
                            Monatsrate (Annuität)
                        </p>
                        <p className="mt-1 text-sm font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                            {formatCurrency(kpis.finanzbedarf.rateMonat, 0)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                            {kpis.finanzbedarf.tilgungLaufzeitJahre} J. ·{' '}
                            {kpis.finanzbedarf.zinssatzProzent} % · Zins M1{' '}
                            {formatCurrency(kpis.finanzbedarf.zinsAnteilMonat1, 0)}
                        </p>
                    </div>
                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                            Gesamtzins über Laufzeit
                        </p>
                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-muted dark:text-slate-300">
                            {formatCurrency(kpis.finanzbedarf.gesamtzins, 0)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-600">
                            Rückzahlung gesamt {formatCurrency(kpis.finanzbedarf.gesamtzahlung, 0)}
                        </p>
                    </div>
                </div>
            </div>

            <div
                className={`mb-6 rounded-xl border px-4 py-3 ${
                    breakEvenMonat
                        ? 'border-[#E6C299]/40 bg-[#E6C299]/10'
                        : 'border-red-500/30 bg-red-950/20'
                }`}
            >
                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                    Break-Even-Indikator
                </p>
                {breakEvenMonat ? (
                    <p className="mt-1 text-sm text-pe-accent-text dark:text-[#E6C299]">
                        Ab Monat <span className="text-lg font-black">{breakEvenMonat}</span> deckt
                        die Anlaufkurve den Totpunkt nachhaltig.
                    </p>
                ) : (
                    <p className="mt-1 text-sm text-red-300/90">
                        Innerhalb der Monate 1–6 erreicht die Anlauf-Prognose den Totpunkt noch nicht
                        nachhaltig.
                    </p>
                )}
            </div>

            <div className="mb-4">
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    {isTakeover
                        ? hasVerbesserung
                            ? 'Übernahme: Planziel + moderate Verbesserung (1–6)'
                            : 'Monatsübersicht Übernahme (1–6)'
                        : 'Anlaufkurve Monate 1–6'}
                </p>
                {isTakeover && hasVerbesserung && (
                    <p className="mb-3 text-xs text-stone-500 dark:text-slate-500">
                        Start = Planziel ({formatCurrency(kpis.planumsatzNetto, 0)} netto) · bis
                        Monat 6 kommt die Planungsrechner-Verbesserung (+
                        {formatCurrency(model.verbesserungUmsatzDeltaNetto, 0)} netto / Monat, Szenario
                        „{aufbau.szenarioName}“) linear dazu.
                    </p>
                )}

                <div className="mb-4 space-y-3">
                    {months.map((row) => (
                        <div
                            key={row.month}
                            className={`rounded-lg border bg-pe-elevated dark:bg-[#141414] p-3 ${
                                breakEvenMonat && row.month >= breakEvenMonat
                                    ? 'border-[#E6C299]/35'
                                    : 'border-pe-border dark:border-[#2a2a2a]'
                            }`}
                        >
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-pe-muted dark:text-slate-300">
                                    Monat {row.month}
                                    {breakEvenMonat === row.month && (
                                        <span className="ml-2 rounded border border-[#E6C299]/50 px-1.5 py-0.5 text-[10px] text-pe-accent-text dark:text-[#E6C299]">
                                            Break-Even
                                        </span>
                                    )}
                                </span>
                                <span className="text-xs text-stone-500 dark:text-slate-500">
                                    {isTakeover ? 'Auslastungsziel' : 'Anlauf-Auslastung'}{' '}
                                    <span className="text-pe-accent-text dark:text-[#E6C299]">
                                        {row.anlaufAuslastung.toFixed(0)} %
                                    </span>
                                    {isTakeover && Math.abs(row.verbesserungAnteilBrutto) > 0.5 && (
                                        <span className="ml-2 text-slate-600">
                                            · Verb. {formatCurrency(row.verbesserungAnteilBrutto, 0)}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="w-24 shrink-0 text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                        Ziel
                                    </span>
                                    <div className="h-2 flex-1 overflow-hidden rounded bg-pe-elevated dark:bg-[#1a1a1a]">
                                        <div
                                            className="h-full rounded bg-slate-500"
                                            style={{ width: barPct(row.umsatzZiel) }}
                                        />
                                    </div>
                                    <span className="w-24 shrink-0 text-right text-xs tabular-nums text-pe-muted dark:text-slate-400">
                                        {formatCurrency(row.umsatzZiel, 0)}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-24 shrink-0 text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                        Prognose
                                    </span>
                                    <div className="h-2 flex-1 overflow-hidden rounded bg-pe-elevated dark:bg-[#1a1a1a]">
                                        <div
                                            className={`h-full rounded ${row.decktTotpunkt ? 'bg-[#E6C299]' : 'bg-orange-500/70'}`}
                                            style={{ width: barPct(row.umsatzPrognose) }}
                                        />
                                    </div>
                                    <span className="w-24 shrink-0 text-right text-xs tabular-nums text-pe-muted dark:text-slate-300">
                                        {formatCurrency(row.umsatzPrognose, 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="overflow-x-auto rounded-xl border border-pe-border dark:border-[#2a2a2a]">
                    <table className="w-full min-w-[860px] border-collapse text-left text-sm">
                        <thead>
                            <tr className="border-b border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                <th className="px-3 py-2.5 font-semibold">Monat</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Umsatzziel</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Prognose</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Fix</th>
                                <th className="px-3 py-2.5 font-semibold text-right">UL</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Personal</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Förderung</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Kreditrate</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Liquidität</th>
                            </tr>
                        </thead>
                        <tbody>
                            {months.map((row) => (
                                <tr
                                    key={`row-${row.month}`}
                                    className={`border-b border-pe-border dark:border-[#222] ${
                                        breakEvenMonat && row.month >= breakEvenMonat
                                            ? 'bg-[#E6C299]/5'
                                            : ''
                                    }`}
                                >
                                    <td className="px-3 py-2.5 font-semibold text-pe-muted dark:text-slate-300">
                                        M{row.month}
                                        {row.investition > 0 && (
                                            <span className="ml-1 text-[10px] font-normal text-stone-500 dark:text-slate-500">
                                                (Invest)
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-pe-muted dark:text-slate-400">
                                        {formatCurrency(row.umsatzZiel, 0)}
                                    </td>
                                    <td
                                        className={`px-3 py-2.5 text-right tabular-nums font-semibold ${
                                            row.decktTotpunkt ? 'text-pe-accent-text dark:text-[#E6C299]' : 'text-orange-400'
                                        }`}
                                    >
                                        {formatCurrency(row.umsatzPrognose, 0)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-pe-muted dark:text-slate-400">
                                        {formatCurrency(row.fixkosten, 0)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-pe-muted dark:text-slate-400">
                                        {formatCurrency(row.unternehmerlohn, 0)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-pe-muted dark:text-slate-400">
                                        {formatCurrency(row.personalkosten, 0)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-pe-muted dark:text-slate-400">
                                        {formatCurrency(row.foerderung, 0)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums text-pe-muted dark:text-slate-400">
                                        {formatCurrency(row.kreditrate, 0)}
                                    </td>
                                    <td
                                        className={`px-3 py-2.5 text-right tabular-nums font-semibold ${
                                            row.liquiditaetssaldo >= 0
                                                ? 'text-pe-accent-text dark:text-[#E6C299]'
                                                : 'text-red-400'
                                        }`}
                                    >
                                        {formatCurrency(row.liquiditaetssaldo, 0)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="mt-4 text-xs text-slate-600">
                Umsatzziel = Kostendeckung × (1 + Liquidität/Rücklagen). Stundensatz = Mindestumsatz
                brutto bei Auslastungsziel (Formel Gesamtleistung).{' '}
                {isTakeover
                    ? hasVerbesserung
                        ? 'Übernahme: Prognose startet am Planziel; Planungsrechner-Hebel werden moderat bis M6 eingearbeitet.'
                        : 'Übernahme: Prognose durchgängig am Planziel — keine Anlauf-Steigerung von null.'
                    : 'Neugründung: Anlauf-Auslastung linear Monat/6.'}
            </p>
        </section>
    );
};

export default PEStartDashboard;
